import React from 'react';
import { 
  ArrowLeft, ClipboardCheck, PackageCheck, Microscope, Truck, CalendarCheck,
  AlertTriangle, CheckCircle, XCircle, AlertCircle, Clock
} from 'lucide-react';
import { inspectionPlans, inspectionTypes, qualityStandards } from '../../data/qualityControlData';

const InspectionPlanDetail = ({ planId, onBack }) => {
  // Find plan
  const plan = inspectionPlans.find(p => p.id === planId);
  
  if (!plan) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-medium mb-2">找不到檢驗計劃</h2>
        <p className="text-gray-500 mb-4">無法找到指定的檢驗計劃</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          返回
        </button>
      </div>
    );
  }
  
  // Get inspection type details
  const inspectionType = inspectionTypes.find(t => t.id === plan.inspection_type) || { name: '其他檢驗', color: '#6B7280' };
  
  // Get quality standard details
  const getStandardDetails = (standardId) => {
    return qualityStandards.find(s => s.id === standardId) || { name: '內部標準' };
  };
  
  // Get inspection type icon
  const getInspectionTypeIcon = (type) => {
    switch(type) {
      case 'incoming':
        return <PackageCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'in-process':
        return <Microscope className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'final':
        return <ClipboardCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      case 'outgoing':
        return <Truck className="h-5 w-5" style={{ color: inspectionType?.color }} />;
      case 'periodic':
        return <CalendarCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
      default:
        return <ClipboardCheck className="h-5 w-5" style={{ color: inspectionType.color }} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <h1 className="text-2xl font-bold">檢驗計劃詳情</h1>
        </div>
        <div 
          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium" 
          style={{ 
            backgroundColor: `${inspectionType.color}20`,
            color: inspectionType.color 
          }}
        >
          {getInspectionTypeIcon(plan.inspection_type)}
          <span className="ml-1">{inspectionType.name}</span>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Plan Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info Card */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">{plan.name}</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-6">{plan.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">基本資訊</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium">計劃編號</div>
                      <div className="text-sm text-gray-600">{plan.id}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">版本</div>
                      <div className="text-sm text-gray-600">{plan.version}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">建立日期</div>
                      <div className="text-sm text-gray-600">{plan.created_date}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">最後更新</div>
                      <div className="text-sm text-gray-600">{plan.last_updated}</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">檢驗資訊</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm font-medium">適用於</div>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {plan.applicable_to.map(item => (
                          <span 
                            key={item} 
                            className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700"
                          >
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">抽樣方法</div>
                      <div className="text-sm text-gray-600">{plan.sampling_method}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">抽樣數量</div>
                      <div className="text-sm text-gray-600">{plan.sampling_size}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium">品質標準</div>
                      <div className="text-sm text-gray-600">
                        {plan.quality_standards.map(standardId => {
                          const standard = getStandardDetails(standardId);
                          return <div key={standardId}>{standard.name}</div>;
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Checkpoints */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">檢驗項目</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <tr>
                    <th className="px-6 py-3 text-left">項目名稱</th>
                    <th className="px-6 py-3 text-left">檢驗方法</th>
                    <th className="px-6 py-3 text-left">檢驗標準</th>
                    <th className="px-6 py-3 text-center">重要性</th>
                    <th className="px-6 py-3 text-left">備註</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {plan.checkpoints.map(checkpoint => (
                    <tr key={checkpoint.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {checkpoint.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {checkpoint.method}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {checkpoint.criteria}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${checkpoint.criticality === 'critical' ? 'bg-red-100 text-red-800' : 
                              checkpoint.criticality === 'major' ? 'bg-orange-100 text-orange-800' : 
                              'bg-yellow-100 text-yellow-800'}`}
                        >
                          {checkpoint.criticality === 'critical' ? '關鍵' : 
                            checkpoint.criticality === 'major' ? '重要' : '一般'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {checkpoint.notes || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Instructions */}
          {plan.instructions && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">檢驗指導說明</h2>
              </div>
              <div className="p-6">
                <div className="prose max-w-none">
                  <p className="text-gray-700">{plan.instructions}</p>
                  
                  {plan.instruction_steps && (
                    <div className="mt-4">
                      <h3 className="text-base font-medium mb-2">檢驗步驟</h3>
                      <ol className="list-decimal pl-5 space-y-2">
                        {plan.instruction_steps.map((step, index) => (
                          <li key={index} className="text-gray-700">{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column */}
        <div className="space-y-6">
          {/* Acceptance Criteria */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-medium">合格標準</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <div className="flex items-center mb-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                    <h3 className="text-sm font-medium">合格條件</h3>
                  </div>
                  <p className="text-sm text-gray-600">{plan.acceptance_criteria.pass}</p>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <AlertCircle className="h-5 w-5 text-amber-500 mr-2" />
                    <h3 className="text-sm font-medium">條件合格條件</h3>
                  </div>
                  <p className="text-sm text-gray-600">{plan.acceptance_criteria.conditional}</p>
                </div>
                
                <div>
                  <div className="flex items-center mb-2">
                    <XCircle className="h-5 w-5 text-red-500 mr-2" />
                    <h3 className="text-sm font-medium">不合格條件</h3>
                  </div>
                  <p className="text-sm text-gray-600">{plan.acceptance_criteria.fail}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Equipment and Tools */}
          {plan.equipment && plan.equipment.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">檢驗設備與工具</h2>
              </div>
              <div className="p-6">
                <ul className="space-y-3">
                  {plan.equipment.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <div className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-medium mr-2 mt-0.5">
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-sm font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-sm text-gray-500">{item.description}</div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          {/* References */}
          {plan.references && plan.references.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">參考文件</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {plan.references.map((reference, index) => (
                  <div key={index} className="p-4">
                    <h3 className="text-sm font-medium">{reference.title}</h3>
                    <div className="text-xs text-gray-500 mt-1">{reference.document_id}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Approval Info */}
          {plan.approval_info && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-lg font-medium">審核資訊</h2>
              </div>
              <div className="p-6">
                <div className="space-y-3">
                  <div>
                    <div className="text-sm font-medium">審核狀態</div>
                    <div className="text-sm text-gray-600">
                      {plan.approval_info.status === 'approved' ? '已核准' : 
                       plan.approval_info.status === 'pending' ? '審核中' : '草稿'}
                    </div>
                  </div>
                  {plan.approval_info.approved_by && (
                    <div>
                      <div className="text-sm font-medium">核准人</div>
                      <div className="text-sm text-gray-600">{plan.approval_info.approved_by}</div>
                    </div>
                  )}
                  {plan.approval_info.approved_date && (
                    <div>
                      <div className="text-sm font-medium">核准日期</div>
                      <div className="text-sm text-gray-600">{plan.approval_info.approved_date}</div>
                    </div>
                  )}
                  {plan.approval_info.next_review && (
                    <div>
                      <div className="text-sm font-medium">下次審核日期</div>
                      <div className="text-sm text-gray-600">{plan.approval_info.next_review}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InspectionPlanDetail;
